/**
 * Body Background Color Utility
 *
 * Scrubs body background color and text color based on scroll position.
 * Animates CSS custom properties for maximum flexibility.
 *
 * CSS Custom Properties animated:
 *   --body-bg-color: current background color
 *   --body-text-color: current text color
 *
 * Usage:
 *   <section data-body-bg-color="primary" data-body-text-color="white">
 *   <section data-body-bg-color="#1a1a1a" data-body-text-color="#ffffff">
 *   <section data-body-bg-color="var(--wp--preset--color--primary)">
 *
 * If data-body-text-color is omitted, it will auto-calculate contrast.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

class BodyBackgroundColor {
	constructor() {
		this.body = document.body;
		this.root = document.documentElement;
		this.triggers = [];
		this.sections = [];
		this.defaultBgColor = null;
		this.defaultTextColor = null;
		this.currentColorClass = null;
	}

	resolveCSSVariable(cssVar) {
		const temp = document.createElement('div');
		temp.style.color = cssVar;
		temp.style.display = 'none';
		document.body.appendChild(temp);
		const computed = window.getComputedStyle(temp).color;
		temp.remove();
		return computed;
	}

	getCurrentBodyBgColor() {
		const bgColor = window.getComputedStyle(this.body).backgroundColor;

		if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent' || !bgColor) {
			const cssValue = this.resolveCSSVariable('var(--wp--preset--color--base)');
			if (cssValue && cssValue !== 'rgba(0, 0, 0, 0)') {
				return cssValue;
			}
			return 'rgb(255, 255, 255)';
		}

		return bgColor;
	}

	getCurrentBodyTextColor() {
		const color = window.getComputedStyle(this.body).color;
		if (color && color !== 'rgba(0, 0, 0, 0)') {
			return color;
		}
		// Try WordPress contrast color
		const cssValue = this.resolveCSSVariable('var(--wp--preset--color--contrast)');
		if (cssValue && cssValue !== 'rgba(0, 0, 0, 0)') {
			return cssValue;
		}
		return 'rgb(0, 0, 0)';
	}

	/**
	 * Calculate relative luminance of a color
	 * Returns value between 0 (black) and 1 (white)
	 */
	getLuminance(color) {
		// Parse rgb/rgba string
		const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
		if (!match) return 0.5;

		const [ , r, g, b ] = match.map(Number);

		// Convert to sRGB
		const toSRGB = (c) => {
			c = c / 255;
			return c <= 0.03928 ? c / 12.92 : Math.pow(( c + 0.055 ) / 1.055, 2.4);
		};

		return 0.2126 * toSRGB(r) + 0.7152 * toSRGB(g) + 0.0722 * toSRGB(b);
	}

	/**
	 * Get contrasting text color (light or dark) for a background
	 */
	getContrastingColor(bgColor) {
		const luminance = this.getLuminance(bgColor);
		// Use WordPress theme colors if available, otherwise black/white
		if (luminance > 0.5) {
			// Light background, need dark text
			const contrast = this.resolveCSSVariable('var(--wp--preset--color--contrast)');
			return contrast !== 'rgba(0, 0, 0, 0)' ? contrast : 'rgb(0, 0, 0)';
		} else {
			// Dark background, need light text
			const base = this.resolveCSSVariable('var(--wp--preset--color--base)');
			return base !== 'rgba(0, 0, 0, 0)' ? base : 'rgb(255, 255, 255)';
		}
	}

	getColorClassName(colorValue) {
		if (!colorValue) return null;

		let slug = '';

		if (colorValue.startsWith('var(--wp--preset--color--')) {
			slug = colorValue.replace('var(--wp--preset--color--', '').replace(')', '');
		} else if (colorValue.startsWith('#')) {
			slug = colorValue.substring(1).toLowerCase();
		} else if (colorValue.startsWith('rgb') || colorValue.startsWith('hsl')) {
			slug = 'custom';
		} else {
			slug = colorValue;
		}

		return `body-color-${slug}--active`;
	}

	getColorValue(colorValue, fallback = null) {
		if (!colorValue) return fallback;

		if (colorValue.startsWith('#')) {
			return colorValue;
		}

		if (colorValue.startsWith('rgb') || colorValue.startsWith('hsl')) {
			return colorValue;
		}

		if (colorValue.startsWith('var(')) {
			return this.resolveCSSVariable(colorValue);
		}

		// Assume it's a WordPress theme.json slug
		const cssVar = `var(--wp--preset--color--${colorValue})`;
		return this.resolveCSSVariable(cssVar);
	}

	updateColorClass(colorIdentifier) {
		if (this.currentColorClass) {
			this.body.classList.remove(this.currentColorClass);
		}

		const newColorClass = this.getColorClassName(colorIdentifier);
		if (newColorClass) {
			this.body.classList.add(newColorClass);
			this.currentColorClass = newColorClass;
		} else {
			this.currentColorClass = null;
		}
	}

	/**
	 * Set CSS custom properties on :root
	 */
	setCSSProperties(bgColor, textColor) {
		this.root.style.setProperty('--body-bg-color', bgColor);
		this.root.style.setProperty('--body-text-color', textColor);
	}

	/**
	 * Detect current scroll position and apply correct colors immediately.
	 */
	initializeState() {
		if (this.sections.length === 0) {
			this.setCSSProperties(this.defaultBgColor, this.defaultTextColor);
			return;
		}

		const scrollY = window.scrollY;
		const viewportCenter = scrollY + window.innerHeight / 2;

		let activeSection = null;
		let activeBgColor = this.defaultBgColor;
		let activeTextColor = this.defaultTextColor;
		let activeColorIdentifier = null;

		// Find which section the viewport center is within
		for (let i = this.sections.length - 1; i >= 0; i--) {
			const section = this.sections[i];
			const rect = section.element.getBoundingClientRect();
			const sectionTop = scrollY + rect.top;

			if (viewportCenter >= sectionTop) {
				activeSection = section;
				activeBgColor = section.bgColor;
				activeTextColor = section.textColor;
				activeColorIdentifier = section.colorIdentifier;
				break;
			}
		}

		// Apply immediately without animation
		gsap.set(this.body, {
			backgroundColor: activeBgColor,
			color: activeTextColor,
		});
		this.setCSSProperties(activeBgColor, activeTextColor);
		this.updateColorClass(activeColorIdentifier);

		// Update active classes
		this.sections.forEach((section) => {
			section.element.classList.remove('bg-active');
		});
		if (activeSection) {
			activeSection.element.classList.add('bg-active');
		}
	}

	init() {
		this.defaultBgColor = this.getCurrentBodyBgColor();
		this.defaultTextColor = this.getCurrentBodyTextColor();

		// Set initial CSS properties
		this.setCSSProperties(this.defaultBgColor, this.defaultTextColor);

		const sectionElements = gsap.utils.toArray('[data-body-bg-color]');

		if (sectionElements.length === 0) return;

		// Build sections array
		sectionElements.forEach((element) => {
			const bgColorIdentifier = element.dataset.bodyBgColor;
			const bgColor = this.getColorValue(bgColorIdentifier, this.defaultBgColor);

			// Get text color - explicit, or auto-contrast
			const textColorIdentifier = element.dataset.bodyTextColor;
			const textColor = textColorIdentifier
					? this.getColorValue(textColorIdentifier, this.defaultTextColor)
					: this.getContrastingColor(bgColor);

			this.sections.push({
				element,
				colorIdentifier: bgColorIdentifier,
				bgColor,
				textColor,
			});
		});

		// Create ScrollTrigger for each section
		this.sections.forEach((section, i) => {
			const { element, colorIdentifier, bgColor, textColor } = section;

			const start = element.dataset.bgStart || 'top center';
			const end = element.dataset.bgEnd || 'top center-=200px';
			const scrubValue = element.dataset.bgScrub !== undefined
					? parseFloat(element.dataset.bgScrub) || true
					: true;

			const tween = gsap.to(this.body, {
				backgroundColor: bgColor,
				color: textColor,
				ease: 'none',
				immediateRender: false,
				scrollTrigger: {
					trigger: element,
					start: start,
					end: end,
					scrub: scrubValue,
					invalidateOnRefresh: true,
					onUpdate: (self) => {
						// Update CSS custom properties during scrub
						const currentBg = gsap.getProperty(this.body, 'backgroundColor');
						const currentText = gsap.getProperty(this.body, 'color');
						this.setCSSProperties(currentBg, currentText);
					},
					onEnter: () => {
						element.classList.add('bg-active');
						this.updateColorClass(colorIdentifier);
					},
					onEnterBack: () => {
						element.classList.add('bg-active');
						this.updateColorClass(colorIdentifier);
					},
					onLeave: () => {
						element.classList.remove('bg-active');
					},
					onLeaveBack: () => {
						element.classList.remove('bg-active');
						if (i === 0) {
							this.updateColorClass(null);
						}
					},
				},
			});

			this.triggers.push(tween.scrollTrigger);
		});

		// Handle return to default after last section
		const lastSection = this.sections[this.sections.length - 1];
		const lastScrub = lastSection.element.dataset.bgScrub !== undefined
				? parseFloat(lastSection.element.dataset.bgScrub) || true
				: true;

		const exitTween = gsap.to(this.body, {
			backgroundColor: this.defaultBgColor,
			color: this.defaultTextColor,
			ease: 'none',
			immediateRender: false,
			scrollTrigger: {
				trigger: lastSection.element,
				start: 'bottom bottom',
				end: '+=50%',
				scrub: lastScrub,
				invalidateOnRefresh: true,
				onUpdate: () => {
					const currentBg = gsap.getProperty(this.body, 'backgroundColor');
					const currentText = gsap.getProperty(this.body, 'color');
					this.setCSSProperties(currentBg, currentText);
				},
				onEnter: () => {
					this.updateColorClass(null);
				},
				onLeaveBack: () => {
					this.updateColorClass(lastSection.colorIdentifier);
				},
			},
		});

		this.triggers.push(exitTween.scrollTrigger);

		ScrollTrigger.sort();
		this.initializeState();

		window.addEventListener('load', () => {
			ScrollTrigger.refresh();
			this.initializeState();
		});
	}

	refresh() {
		ScrollTrigger.refresh();
		this.initializeState();
	}

	destroy() {
		this.triggers.forEach((trigger) => trigger.kill());
		this.triggers = [];
		this.sections = [];

		if (this.currentColorClass) {
			this.body.classList.remove(this.currentColorClass);
			this.currentColorClass = null;
		}

		gsap.set(this.body, {
			backgroundColor: this.defaultBgColor,
			color: this.defaultTextColor,
		});
		this.setCSSProperties(this.defaultBgColor, this.defaultTextColor);
	}
}

const bodyBackgroundColor = new BodyBackgroundColor();

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => bodyBackgroundColor.init());
} else {
	bodyBackgroundColor.init();
}

export default bodyBackgroundColor;
export { BodyBackgroundColor };
